import { useMemo, useRef, useState } from "react";
import useSWR, { SWRResponse } from "swr";
import {
  isVideoSegment,
  type Segment,
  type VideoSegment,
} from "@memento/types/segment";
import { notEmpty } from "@memento/library/type-witchcraft";
import {
  getIsEnoughSegmentsToRenderV2,
  getIsEnoughSegmentsToRenderV3,
  sortSegments,
} from "@memento/library/segment";
import {
  useAuthenticatedFetcher,
  getLoadingStatus,
  LoadingStatus,
} from "src/libs/fetcher";
import { getCardSegmentsEndpointUrl } from "src/libs/api-endpoint-lib";
import { fillSortOrderGaps } from "src/components/Dashboard/dashboard-utils";
import { UseLegacyRequest, useLegacyRequest } from "./useLegacyRequest";
import { getNewSortOrderAfterDrop } from "src/libs/drag-drop-lib";
import { useFeatureFlags } from "src/contexts/FeatureFlagProvider";
import { nConcurrent } from "@memento/library/promise";

function getMaxSortOrder(segments: Segment[]): number {
  return segments.reduce((acc, segment) => {
    return segment.sortOrder > acc ? segment.sortOrder : acc;
  }, 0);
}

/**
 * Returns true if the segment is being moved before any locked segments (i.e.
 * if there is a single locked segment in the list of segments, before this one).
 */
function getIsSegmentMovedAfterLockedSegments({
  droppedSegment,
  allSegmentsSorted,
}: {
  droppedSegment: Segment;
  allSegmentsSorted: Segment[];
}) {
  const segmentIndex = allSegmentsSorted.findIndex(
    (x) => x.segmentId === droppedSegment.segmentId
  );

  if (segmentIndex === -1) {
    // this should never happen, but still good to check
    return false;
  }

  const segmentIndexOfFirstLockedSegment = allSegmentsSorted.findIndex((x) =>
    Boolean(x.isLocked)
  );

  if (segmentIndexOfFirstLockedSegment === -1) {
    // no locked segments, so not possible to move a segment after locked
    // segments
    return false;
  }

  if (segmentIndexOfFirstLockedSegment < segmentIndex) {
    return true;
  } else {
    return false;
  }
}

export type UseCardSegmentsResult = {
  allSegments: Segment[];
  outtakes: Segment[];
  activeSegments: Segment[];
  saveSegments: (newSegments: Segment[]) => Promise<void>;
  updateDeleteSegment: (segment: Segment, isDeleted: boolean) => Promise<void>;
  moveActiveSegment: (args: {
    /** The original sort order position of the segment */
    oldSortOrder: number;
    /** The new sort order position of the segment */
    newSortOrder: number;
  }) => Promise<void>;
  maxSortOrder: number;
  maxActiveSortOrder: number;
  maxUnlockedSortOrder: number;
  haveActiveSegments: boolean;
  haveOuttakes: boolean;
  isEnoughSegmentsToRender: boolean;
  /** True while one or more segment saves are still in flight (queued or being
   * persisted to the server). The UI updates optimistically, so this lets
   * callers wait for the server to catch up before allowing a render. */
  isSavingSegments: boolean;
  reloadSegments: () => void;
  status: LoadingStatus;
  // properties only used for v2 compilations
  v2: {
    activeVideoSegments: VideoSegment[];
    haveActiveVideoSegments: boolean;
    isEnoughVideoSegmentsToRender: boolean;
  };
};

function useFetchCardSegments(cardId: string | null, cardUrl: string | null) {
  const fetcher = useAuthenticatedFetcher();
  const key =
    cardId && cardUrl ? getCardSegmentsEndpointUrl({ cardId, cardUrl }) : null;

  const { data, error, mutate }: SWRResponse<Segment[], Error> = useSWR(
    key,
    (url: string) => fetcher(url)
  );

  return {
    data,
    error,
    mutate,
    status: getLoadingStatus(key, data, error),
  };
}

export type UseFetchCardSegments = (
  cardId: string | null,
  cardUrl: string | null,
  options?: {
    // options used by offline hooks
    offline?: { occasionId?: string };
  }
) => ReturnType<typeof useFetchCardSegments>;

export const useCardSegments = (
  cardId: string | null,
  cardUrl: string | null
) => {
  const { saveSegments: saveSegmentsRequest } = useLegacyRequest();

  return useCardSegmentsProxy(cardId, cardUrl, {
    saveSegmentsRequest,
    useFetchCardSegmentsHook: useFetchCardSegments,
  });
};

type UseCardSegmentsProxyOptions = {
  saveSegmentsRequest: UseLegacyRequest["saveSegments"];
  useFetchCardSegmentsHook: UseFetchCardSegments;
  // options only used by offline hooks
  offlineOptions?: {
    occasionId?: string;
  };
};

// this is a proxy for useCardSegments that allows us to pass in the
// queries/mutations as dependencies (e.g. if we wanted an "offline" version)
export const useCardSegmentsProxy = (
  cardId: string | null,
  cardUrl: string | null,
  {
    saveSegmentsRequest,
    useFetchCardSegmentsHook,
    offlineOptions,
  }: UseCardSegmentsProxyOptions
) => {
  const { featureConfig } = useFeatureFlags();

  // Track in-flight saves so callers can tell when the optimistic UI has been
  // fully persisted to the server. The ref holds the live count; the state
  // mirrors it as a boolean to trigger re-renders.
  const pendingSavesRef = useRef(0);
  const [isSavingSegments, setIsSavingSegments] = useState(false);

  const { data, error, mutate, status } = useFetchCardSegmentsHook(
    cardId,
    cardUrl,
    offlineOptions?.occasionId
      ? { offline: { occasionId: offlineOptions.occasionId } }
      : undefined
  );

  const saveSegmentsThrottled: UseLegacyRequest["saveSegments"] =
    useMemo(() => {
      // We only want to allow one call to be executed at a time otherwise
      // we'll have race conditions where early calls could be handled after
      // later calls.
      return nConcurrent(1, saveSegmentsRequest);
    }, [
      // saveSegmentsRequest needs to be a dep so that we clear the debounced
      // function when it changes
      saveSegmentsRequest,
    ]);

  // Build the result without isSavingSegments so that toggling the saving state
  // doesn't recompute (and create new references for) saveSegments,
  // updateDeleteSegment, moveActiveSegment, etc. isSavingSegments is merged in
  // separately below.
  const baseResult = useMemo<Omit<
    UseCardSegmentsResult,
    "isSavingSegments"
  > | null>(() => {
    if (!data) {
      return null;
    }

    const allSegments = data;

    const outtakes = allSegments.filter((segment) => segment.isDeleted);
    const haveOuttakes = outtakes.length > 0;

    const activeSegments = allSegments.filter((segment) => !segment.isDeleted);
    const haveActiveSegments = activeSegments.length > 0;
    const isEnoughSegmentsToRender = getIsEnoughSegmentsToRenderV3(
      activeSegments,
      featureConfig.render.v3.minSegmentCountToRender
    );

    const unlockedSegments = allSegments.filter((segment) => !segment.isLocked);

    const activeVideoSegments = activeSegments.filter(isVideoSegment);

    const haveActiveVideoSegments = activeVideoSegments.length > 0;
    const isEnoughVideoSegmentsToRender = getIsEnoughSegmentsToRenderV2(
      activeVideoSegments,
      featureConfig.render.v2.minSegmentCountToRender
    );

    // the maximum sort order of all segments (NOTE: it is a sortOrder that
    // one of the segments already has)
    const maxSortOrder = getMaxSortOrder(allSegments);

    // the maximum sort order of active segments (NOTE: it is a sortOrder that
    // one of the segments already has)
    const maxActiveSortOrder = getMaxSortOrder(activeSegments);

    // the maximum sort order of unlocked segments (NOTE: it is a sortOrder that
    // one of the segments already has)
    const maxUnlockedSortOrder = getMaxSortOrder(unlockedSegments);

    // Save segments in batch, so we can mutate them all at once. Note that
    // this function will fill gaps in sortOrder.
    async function saveSegments(newSegments: Segment[]) {
      if (!cardId) {
        return;
      }

      if (!newSegments.length) {
        return;
      }

      const oldSegments = allSegments;

      const oldSegmentSortOrders = oldSegments.reduce<Record<string, number>>(
        (acc, segment) => {
          acc[segment.segmentId] = Number(segment.sortOrder);
          return acc;
        },
        {}
      );

      // if updating these segments causes a re-ordering, then we'll need
      // to make sure all "gaps" are filled in the sort order

      const saveSegmentIds = newSegments.map((x) => x.segmentId);

      // all the segments with modifications (can be passed to SWR mutate)
      const updatedSegments = fillSortOrderGaps([
        // get rid of the segments we're updating
        ...oldSegments.filter((x) => !saveSegmentIds.includes(x.segmentId)),
        // so we can add them back
        ...newSegments,
      ]);

      // the ids of segments that had their sortOrder change (could overlap
      // with the segments we're saving)
      const modifiedSortOrderSegmentIds = updatedSegments
        .map((segment) => {
          if (oldSegmentSortOrders[segment.segmentId] !== segment.sortOrder) {
            return segment.segmentId;
          } else {
            return null;
          }
        })
        .filter(notEmpty);

      // the segments we actually want to save are the ones originally requested
      // to be saved and any with a new sortOrder
      const modifiedSegmentIds = new Set([
        ...saveSegmentIds,
        ...modifiedSortOrderSegmentIds,
      ]);

      // save the segments

      // give SWR the updated segments
      mutate(updatedSegments, false);

      const segmentsToSave = updatedSegments.filter((segment) =>
        modifiedSegmentIds.has(segment.segmentId)
      );

      // Both statements are inside the try so the finally cleans up if either
      // throws. The increment comes before setIsSavingSegments so the finally's
      // decrement always has a matching increment — if setIsSavingSegments threw
      // first, the finally would over-decrement the counter.
      try {
        pendingSavesRef.current += 1;
        setIsSavingSegments(true);
        await saveSegmentsThrottled({ cardId }, segmentsToSave);
      } finally {
        pendingSavesRef.current -= 1;
        if (pendingSavesRef.current === 0) {
          setIsSavingSegments(false);
        }
      }
    }

    async function updateDeleteSegment(segment: Segment, isDeleted: boolean) {
      // no change
      if (segment.isDeleted === isDeleted) {
        return;
      }

      // put the segment at the end
      const sortOrder = maxSortOrder + 1;

      const updatedSegment = {
        ...segment,
        isDeleted,
        sortOrder,
      };

      await saveSegments([updatedSegment]);
    }

    const moveActiveSegment: UseCardSegmentsResult["moveActiveSegment"] =
      async ({ oldSortOrder, newSortOrder }) => {
        const droppedSegment = activeSegments.find(
          (segment) => segment.sortOrder === oldSortOrder
        );

        if (!droppedSegment) {
          return;
        }

        // if the segment is locked, then we don't want to allow moving it
        if (Boolean(droppedSegment.isLocked)) {
          return;
        }

        const reorderedSegmentsInfo = activeSegments.map((segment) => {
          const originalOrder = segment.sortOrder;

          const finalSortOrder = getNewSortOrderAfterDrop({
            droppedItemOldSortOrder: oldSortOrder,
            droppedItemNewSortOrder: newSortOrder,
            thisItemOldSortOrder: originalOrder,
          });

          const updatedSegment = {
            ...segment,
            sortOrder: finalSortOrder,
          };

          return {
            segment: updatedSegment,
            isMoved: originalOrder !== finalSortOrder,
          };
        });

        const updatedSegments = reorderedSegmentsInfo
          .filter(({ isMoved }) => {
            // only save moved segments
            return isMoved;
          })
          .map(({ segment }) => segment);

        const allSegmentsSortedAfterReordering = sortSegments(
          reorderedSegmentsInfo.map(({ segment }) => segment)
        );

        if (
          getIsSegmentMovedAfterLockedSegments({
            droppedSegment,
            allSegmentsSorted: allSegmentsSortedAfterReordering,
          })
        ) {
          // If this segment is being moved after any locked segments, don't
          // proceed with the move
          return;
        }

        await saveSegments(updatedSegments);
      };

    return {
      allSegments,
      outtakes,
      activeSegments,
      saveSegments,
      updateDeleteSegment,
      moveActiveSegment,
      maxSortOrder,
      maxActiveSortOrder,
      maxUnlockedSortOrder,
      haveActiveSegments,
      haveOuttakes,
      isEnoughSegmentsToRender,
      reloadSegments: () => mutate(),
      status,
      v2: {
        activeVideoSegments,
        haveActiveVideoSegments,
        isEnoughVideoSegmentsToRender,
      },
    };
  }, [
    cardId,
    status,
    data,
    mutate,
    saveSegmentsThrottled,
    featureConfig.render.v2.minSegmentCountToRender,
    featureConfig.render.v3.minSegmentCountToRender,
  ]);

  const result = useMemo<UseCardSegmentsResult | null>(
    () => (baseResult ? { ...baseResult, isSavingSegments } : null),
    [baseResult, isSavingSegments]
  );

  return {
    result,
    error,
    status,
    mutate,
  };
};
